import {Component, DestroyRef, inject} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {FormBuilder, ReactiveFormsModule, Validators} from "@angular/forms";
import {Store} from "@ngrx/store";
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {SharedModule} from "../../../shared/shared.module";
import {FundReportActions} from "../../store/fund.actions";
import {selectSelectedFundReportDates} from "../../store/fund-report/fund-report.selectors";
import {DateUtil} from "../../../shared/utils/date-util";
import {reportDateUniqueValidator} from "../../../shared/utils/form-validators";

/**
 * "Add Fund Report" dialog (WI-1239): opened on top of the Manage Fund Report dialog (which stays open).
 * Uses the shared report-date-picker (quarter-end / specific past date) and dispatches the create action.
 */
@Component({
    selector: "valumize-add-fund-report-dialog",
    templateUrl: "./add-fund-report-dialog.component.html",
    styleUrls: ["./add-fund-report-dialog.component.scss"],
    standalone: true,
    imports: [ReactiveFormsModule, MatDialogModule, MatButtonModule, MatIconModule, SharedModule]
})
export class AddFundReportDialogComponent {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly destroyRef = inject(DestroyRef);
    private readonly dialogRef = inject(MatDialogRef<AddFundReportDialogComponent>);
    private readonly data = inject<{ fundId: number }>(MAT_DIALOG_DATA);

    private existingReportDates: string[] = [];

    reportForm = this.formBuilder.group({
        reportDate: this.formBuilder.control<Date | null>(null, {
            validators: [Validators.required, reportDateUniqueValidator(() => this.existingReportDates)]
        })
    });

    constructor() {
        // The dialog can open before the fund's reports have loaded, so the validator tracks the store
        // instead of a snapshot taken at open time — otherwise a late-arriving report would not be
        // detected and a duplicate report date would be accepted (WI-1239).
        this.store.select(selectSelectedFundReportDates)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(reportDates => {
                this.existingReportDates = reportDates;
                this.reportForm.controls.reportDate.updateValueAndValidity({emitEvent: false});
            });
    }

    addReport(): void {
        const reportDate = DateUtil.toIsoDate(this.reportForm.controls.reportDate.value);
        if (!this.reportForm.valid || !reportDate) {
            return;
        }
        this.store.dispatch(FundReportActions.create({fundId: this.data.fundId, reportDate}));
        this.dialogRef.close();
    }
}
